import { cva } from 'class-variance-authority'

import { OverlayLayer } from '#components/_primitives/overlay-styles'

import { ToastPosition, ToastVariant } from './types'

/*
 * Safe-area-aware offsets.
 *
 * max(0.75rem, env(safe-area-inset-*)) keeps a 12px margin on devices
 * without a notch/home indicator while stepping out of the unsafe zone
 * on devices that have one. env() returns 0 on non-notched devices, so
 * max() resolves to the 0.75rem baseline there.
 *
 * 12px (not 8px) because the page container already has 8px padding
 * around its content; sitting the toast at 8px would visually butt
 * against the page edge. 12px gives 4px breathing room.
 */
const insetTop = 'top-[max(0.75rem,env(safe-area-inset-top))]'
const insetRight = 'right-[max(0.75rem,env(safe-area-inset-right))]'
const insetBottom = 'bottom-[max(0.75rem,env(safe-area-inset-bottom))]'
const insetLeft = 'left-[max(0.75rem,env(safe-area-inset-left))]'

// Above the DS overlays: a toast fired from an open dialog would otherwise
// paint behind it.
export const viewportVariants = cva(
  [
    'fixed',
    OverlayLayer.Toast,
    'flex w-full max-w-[360px] p-4 outline-none pointer-events-none',
  ],
  {
    defaultVariants: {
      position: ToastPosition.BottomRight,
    },
    variants: {
      position: {
        [ToastPosition.TopLeft]: `${insetTop} ${insetLeft}`,
        [ToastPosition.TopCenter]: `${insetTop} left-1/2 -translate-x-1/2`,
        [ToastPosition.TopRight]: `${insetTop} ${insetRight}`,
        [ToastPosition.BottomLeft]: `${insetBottom} ${insetLeft}`,
        [ToastPosition.BottomCenter]: `${insetBottom} left-1/2 -translate-x-1/2`,
        [ToastPosition.BottomRight]: `${insetBottom} ${insetRight}`,
      },
    },
  },
)

/**
 * CSS custom properties provided by Base UI:
 *   --toast-index         Position in the toast list (0 = frontmost)
 *   --toast-height        Measured natural height of the toast
 *   --toast-frontmost-height  Height of the frontmost toast
 *   --toast-offset-y      Vertical offset when expanded
 *   --toast-swipe-movement-x  Horizontal swipe delta
 *   --toast-swipe-movement-y  Vertical swipe delta
 *
 * Derived custom properties (computed per-toast):
 *   --gap     Spacing between expanded toasts
 *   --peek    Visible peek height in collapsed stack
 *   --scale   Scale factor for collapsed depth effect
 *   --shrink  How much height the scale reduces
 *   --height  Resolved height (frontmost or own)
 */
const rootBaseStyles = [
  '[--gap:0.5rem]',
  '[--peek:0.5rem]',
  '[--scale:calc(max(0,1-(var(--toast-index)*0.1)))]',
  '[--shrink:calc(1-var(--scale))]',
  '[--height:var(--toast-frontmost-height,var(--toast-height))]',
  // Layout
  'z-[calc(1000-var(--toast-index))]',
  'w-full select-none pointer-events-auto',
  // Varying heights: collapsed toasts match frontmost height, expanded show natural height
  'h-[var(--toast-frontmost-height)]',
  'data-[expanded]:h-[var(--toast-height)]',
  // Expanded transform: swipe + computed offset
  'data-[expanded]:[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--offset-y)))]',
  // Transitions. Opacity + height are vestibular-safe and always animate.
  // The transform transition (slide + stack reflow) is gated behind
  // motion-safe so reduced-motion users get a static fade only.
  '[transition:opacity_0.45s,height_0.15s]',
  'motion-safe:[transition:transform_0.45s_var(--ease-out-quart),opacity_0.45s,height_0.15s]',
  // Exit opacity
  'data-[ending-style]:opacity-0',
  'data-[limited]:opacity-0',
  // Exit with swipe direction overrides (identical for all positions)
  'data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
  'data-[expanded]:data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
  'data-[ending-style]:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
  'data-[expanded]:data-[ending-style]:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
  'data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
  'data-[expanded]:data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
  'data-[ending-style]:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
  'data-[expanded]:data-[ending-style]:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
]

/** Bottom-anchored root styles. Toasts stack upward. */
const rootBottomStyles = [
  // Offset-y: negative to stack upward
  '[--offset-y:calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))]',
  // Positioning
  'absolute right-0 bottom-0 left-auto',
  'origin-bottom',
  // Collapsed transform: scale + swipe + peek offset (negative direction)
  '[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))]',
  // Enter animation: slide up from below
  'data-[starting-style]:[transform:translateY(150%)]',
  // Exit animation (no swipe): slide down
  '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(150%)]',
  // Gap filler below each toast for hover detection
  "after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']",
]

/** Top-anchored root styles. Toasts stack downward. */
const rootTopStyles = [
  // Offset-y: positive to stack downward
  '[--offset-y:calc(var(--toast-offset-y)+calc(var(--toast-index)*var(--gap))+var(--toast-swipe-movement-y))]',
  // Positioning
  'absolute right-0 top-0 left-auto',
  'origin-top',
  // Collapsed transform: scale + swipe + peek offset (positive direction)
  '[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--peek))+(var(--shrink)*var(--height))))_scale(var(--scale))]',
  // Enter animation: slide down from above
  'data-[starting-style]:[transform:translateY(-150%)]',
  // Exit animation (no swipe): slide up
  '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(-150%)]',
  // Gap filler above each toast for hover detection
  "after:absolute after:bottom-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']",
]

const rootTopCombined = [...rootBaseStyles, ...rootTopStyles]
const rootBottomCombined = [...rootBaseStyles, ...rootBottomStyles]

/** Returns position-specific root styles (base + top or bottom). */
export function getRootStylesForPosition(
  position: ToastPosition,
): readonly string[] {
  switch (position) {
    case ToastPosition.TopLeft:
    case ToastPosition.TopCenter:
    case ToastPosition.TopRight:
      return rootTopCombined
    default:
      return rootBottomCombined
  }
}

export const iconContainerVariants = cva(
  ['flex items-center justify-center size-6 rounded-md shrink-0'],
  {
    defaultVariants: {
      variant: ToastVariant.Informational,
    },
    variants: {
      variant: {
        [ToastVariant.Informational]:
          'bg-surface-processing-secondary text-icon-processing-weak',
        [ToastVariant.Success]:
          'bg-surface-success-secondary text-icon-success-weak',
        [ToastVariant.Error]:
          'bg-surface-danger-secondary text-icon-danger-weak',
      },
    },
  },
)
